import { useState, type FormEvent } from 'react';
import { PlatformApiError, PlatformUnreachableError, type Participant } from '@sfc/sdk';
import { platform } from '../api/client';

/**
 * One form, two ways in.
 *
 * There is no sign-up tab, because there is no difference between signing up
 * and signing in: completing a link is both, and a screen offering them
 * separately would ask people to answer a question the system does not have.
 *
 * The password field is optional and says so. It cannot be revealed only to
 * the people who have one -- a form that changed shape for a known address
 * would be a way to ask this product who its members are -- so it is simply
 * there, blank, and leaving it blank asks for a link. Most people will.
 */
export function SignInScreen({
  onSent,
  onSignedIn,
}: {
  onSent: (email: string) => void;
  onSignedIn: (participant: Participant) => void;
}): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setProblem(undefined);
    setBusy(true);
    try {
      if (password) {
        onSignedIn(await platform.match.signInWithPassword(email.trim(), password));
        return;
      }
      await platform.match.requestSignInLink(email.trim());
      onSent(email.trim());
    } catch (error) {
      setProblem(describe(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="screen">
      <div className="pane">
        <header className="pane__head">
          <span className="mark" aria-hidden="true">
            SFC
          </span>
          <h1>Find the people you are missing</h1>
          <p className="lede">
            Post what you are building and who you need, or swipe through ideas looking for what you
            do.
          </p>
        </header>

        <form className="form" onSubmit={submit}>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="off"
              autoCorrect="off"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
            />
          </div>

          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Only if you have set one"
              data-testid="password"
            />
          </div>

          {problem ? (
            <p className="notice notice--problem" role="alert" data-testid="sign-in-problem">
              {problem}
            </p>
          ) : null}

          <button className="button button--primary" type="submit" disabled={busy || !email.trim()}>
            {busy ? (password ? 'Signing in…' : 'Sending…') : password ? 'Sign in' : 'Email me a link'}
          </button>

          <p className="fine">
            {password
              ? 'Forgotten it? Clear the password and we will email you a link instead.'
              : 'Leave the password blank and we will email you a link. Opening it signs you in, and if you have not been here before it is also how your account is made.'}
          </p>
        </form>
      </div>
    </main>
  );
}

function describe(error: unknown): string {
  if (error instanceof PlatformUnreachableError) {
    return 'We could not reach the server. Check your connection and try again.';
  }
  if (error instanceof PlatformApiError) {
    // 429 carries its own sentence, and it is a useful one.
    if (error.status === 429) return error.message;
    if (error.status === 400) return 'That does not look like an email address.';
  }
  return 'Something went wrong. Try again.';
}
